// import React, { useState } from "react";
import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, useTheme, Paper, Dialog, DialogContent } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import ProgressiveAnalysisTracker from "../casting/ProgressiveAnalysisTracker";
import FormulationTable from "../../../shared/modules/FormulationTable";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { PlasticsQuestions } from "./questions";
import { PlasticsPlayBook } from "./playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { theme } from "../../../design/theme";
import { apiFetch } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";

const { perlColors, perlTypography } = theme.custom;
const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "ohp"];
// const normalizeKey = (key = "") => key.toLowerCase().replace(/[\s_-]+/g, "");
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
// const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "logistics","total"].map(normalizeKey));
// const ALLOWED_BREAKDOWN_KEYS = new Set(DEFAULT_BREAKDOWN_ORDER.map(normalizeKey));
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  ohp: "Overhead & Profits",
};

const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });

  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });

  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};

// Handling assumption section locally and rendering the component here
function renderAssumptions({
  assumptions = [],
  productData,
  formulaResult,
}) {

  return (
    <Box>
      {assumptions && assumptions.length > 0 && (
        <Paper
          sx={{
            p: 3,
            bgcolor: perlColors.surface,
            border: `1px solid ${perlColors.border}`,
            borderRadius: 2,
            mt: 3,
          }}
        >
          {(() => {
            const bottomSectionKeywords = [
              "Machine tonnage:",
              "Tonnage:",
              "Cycle time:",
              "Cycle Time(sec):",
              "Machine rate:",
              "OHP rate:",
              "No. of operations:",
              "Material price:",
              "Material Price:",
              "Labour cost:",
              "Labour Rate:",
              "Glue flap:",
              "Board cost per box:",
              "Ink cost per box:",
              "Material spec:",
              "Ink cost per_box:",
              "Total conversion cost:",
              "OHP:",
            ];

            // Case-insensitive matching for keywords
            const topAssumptions = assumptions.filter(
              (assumption) => {
                // Ensure assumption is a string
                const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                return !bottomSectionKeywords.some((keyword) =>
                  assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
                );
              }
            );
            let bottomAssumptions = assumptions.filter((assumption) => {
              // Ensure assumption is a string
              const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
              return bottomSectionKeywords.some((keyword) =>
                assumptionStr.toLowerCase().startsWith(keyword.toLowerCase())
              );
            });

            // Map values from formulaResult if available
            if (formulaResult) {
              // Extract required_ton from formulaResult.MachineTonnage.required_ton
              const requiredTonnage = formulaResult.MachineTonnage?.required_ton;
              if (requiredTonnage !== undefined && requiredTonnage !== null) {
                // Remove existing "Machine tonnage:" or "Tonnage:" entries and add new one
                bottomAssumptions = bottomAssumptions.filter((assumption) => {
                  const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                  return !assumptionStr.toLowerCase().startsWith("machine tonnage:") &&
                    !assumptionStr.toLowerCase().startsWith("tonnage:");
                });
                bottomAssumptions.push(`Machine tonnage: ${parseFloat(requiredTonnage).toFixed(2)} tons`);
              }

              // Extract total_cycle_s from formulaResult.CycleTime.total_cycle_s
              const totalCycleTime = formulaResult.CycleTime?.total_cycle_s;
              if (totalCycleTime !== undefined && totalCycleTime !== null) {
                // Remove existing "Cycle time:" or "Cycle Time(sec):" entries and add new one
                bottomAssumptions = bottomAssumptions.filter((assumption) => {
                  const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                  return !assumptionStr.toLowerCase().startsWith("cycle time") &&
                    !assumptionStr.toLowerCase().startsWith("cycle time(sec):");
                });
                bottomAssumptions.push(`Cycle Time(sec): ${parseFloat(totalCycleTime).toFixed(2)}`);
              }
            }

            // Build product information from productData if available
            const buildProductInfo = () => {
              if (!productData) {
                // Fallback to topAssumptions if productData not available
                return topAssumptions.map((assumption) => assumption);
              }

              const productInfo = [];

              // Product Name
              if (productData.productName) {
                productInfo.push(`Product Name: ${productData.productName}`);
              }

              // Material
              if (productData.materialSelection) {
                productInfo.push(`Material: ${productData.materialSelection}`);
              }

              // Batch Size
              if (productData.batchSize) {
                productInfo.push(`Batch Size: ${productData.batchSize}`);
              }

              // Part Dimensions
              const dims = productData.partWeightDimensions;
              if (dims) {
                const dimParts = [];
                if (dims.partLength) dimParts.push(`L: ${dims.partLength}mm`);
                if (dims.partWidth) dimParts.push(`W: ${dims.partWidth}mm`);
                if (dims.partHeight) dimParts.push(`H: ${dims.partHeight}mm`);
                if (dims.finishWeight) dimParts.push(`Weight: ${dims.finishWeight}g`);
                if (dimParts.length > 0) {
                  productInfo.push(`Part Dimensions: ${dimParts.join(", ")}`);
                }
              }

              // Part Details (Plastics-specific fields)
              const details = productData.partDetails;
              if (details) {
                const detailParts = [];
                // Plastics-specific fields
                if (details.noOfCavities !== undefined && details.noOfCavities !== null) {
                  detailParts.push(`Cavities: ${details.noOfCavities}`);
                }
                if (details.regrindPct !== undefined && details.regrindPct !== null) {
                  detailParts.push(`Regrind: ${details.regrindPct}%`);
                }
                if (details.runnerType) {
                  detailParts.push(`Runner Type: ${details.runnerType}`);
                }
                // Use wallThickness if available, otherwise avgWallThickness
                const wallThickness = details.wallThickness !== undefined && details.wallThickness !== null
                  ? details.wallThickness
                  : (details.avgWallThickness !== undefined && details.avgWallThickness !== null
                    ? details.avgWallThickness
                    : null);
                if (wallThickness !== null) {
                  detailParts.push(`Wall Thickness: ${wallThickness}mm`);
                }
                if (detailParts.length > 0) {
                  productInfo.push(`Part Details: ${detailParts.join(", ")}`);
                }
              }

              // Manufacturing Country
              if (productData.manufacturingCountry) {
                productInfo.push(
                  `Manufacturing Country: ${productData.manufacturingCountry}`
                );
              }

              // Don't add remainingAssumptions - only show actual product data
              // This prevents raw values like "50", "20", "USA" from appearing
              return productInfo;
            };

            const productInfoItems = buildProductInfo();

            return (
              <Box sx={{ display: "flex", gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 2,
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "18px",
                      fontFamily: perlTypography.family,
                      letterSpacing: "0.3px",
                      color: perlColors.textPrimary,
                    }}
                  >
                    Product Information
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {productInfoItems.map((item, idx) => {
                      // Ensure item is a string (handle objects, arrays, etc.)
                      const itemStr = typeof item === 'string'
                        ? item
                        : (typeof item === 'object' && item !== null
                          ? (item.value || item.name || item.reason || JSON.stringify(item))
                          : String(item || ''));
                      return (
                        <Typography
                          key={idx}
                          component="li"
                          variant="body2"
                          sx={{
                            mb: 1,
                            fontSize: "14px",
                            fontFamily: perlTypography.family,
                            color: perlColors.textSecondary,
                            lineHeight: "20px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {itemStr}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>

                {bottomAssumptions.length > 0 && (
                  <Box
                    sx={{
                      width: "1px",
                      bgcolor: perlColors.border || "rgba(255, 255, 255, 0.12)",
                      alignSelf: "stretch",
                    }}
                  />
                )}

                {bottomAssumptions.length > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: "18px",
                        fontFamily: perlTypography.family,
                        letterSpacing: "0.3px",
                        color: perlColors.textPrimary,
                      }}
                    >
                      Assumptions
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                      {bottomAssumptions.map((assumption, idx) => {
                        // Ensure assumption is a string (handle objects, arrays, etc.)
                        const assumptionStr = typeof assumption === 'string'
                          ? assumption
                          : (typeof assumption === 'object' && assumption !== null
                            ? (assumption.value || assumption.name || assumption.reason || JSON.stringify(assumption))
                            : String(assumption || ''));
                        return (
                          <Typography
                            key={idx}
                            component="li"
                            variant="body2"
                            sx={{
                              mb: 1,
                              fontSize: "14px",
                              fontFamily: perlTypography.family,
                              color: perlColors.textSecondary,
                              lineHeight: "20px",
                              letterSpacing: "0.3px",
                            }}
                          >
                            {assumptionStr}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}
        </Paper>
      )}

    </Box>
  )
}

// Helper function to calculate OHP rate from costData
// OHP Rate = (OHP / Total) * 100 - matches the percentage shown in cost category table
const calculateOhpRateFromCostData = (costData) => {
  if (!costData || !costData.breakdown) {
    return 0;
  }
  const ohp = Number(costData.breakdown.ohp || 0);
  const total = Number(
    costData.breakdown.total_should_cost ||
    costData.breakdown.total ||
    0
  );

  // Calculate total from breakdown if not explicitly provided
  const calculatedTotal = total || (
    Number(costData.breakdown.material || 0) +
    Number(costData.breakdown.process || 0) +
    ohp
  );

  if (calculatedTotal > 0 && ohp > 0) {
    return Number(((ohp / calculatedTotal) * 100).toFixed(2));
  }
  return 0;
};

export default function PlasticsPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [keyInsightsData, setKeyInsightsData] = useState(null);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  // Functional groups for formulation
  const functionalGroups = [
    "Bulk ingredient",
    "Sweetners",
    "Oil and fats",
    "Active ingredient",
    "Leavening agents",
    "Flavour enhancers",
    "Flavours",
    "Colours",
    "Preservatives",
    "Emulsifiers",
    "Stabilizers",
    "Thickeners",
    "Acidulants",
    "Vitamins and minerals",
    "Other",
  ];

  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));

    // Initialize playBookData from productData and costData when starting playbook section
    if (activeSection === "playbook" && productData) {
      const detailsData = productData.partDetails || {};
      const calculatedOhpRate = costData ? calculateOhpRateFromCostData(costData) : 0;

      // Extract cycle time from costData (from backend response or formulaResult)
      let cycleTimeSeconds = 0;
      if (costData?.cycleTime_s !== undefined && costData.cycleTime_s !== null) {
        cycleTimeSeconds = parseFloat(costData.cycleTime_s) || 0;
      } else if (costData?.formulaResult?.CycleTime?.totalCycleTime_s !== undefined) {
        cycleTimeSeconds = parseFloat(costData.formulaResult.CycleTime.totalCycleTime_s) || 0;
      } else if (costData?._originalResult?.cycleTime_s !== undefined) {
        cycleTimeSeconds = parseFloat(costData._originalResult.cycleTime_s) || 0;
      } else if (costData?._originalResult?.formulaResult?.CycleTime?.totalCycleTime_s !== undefined) {
        cycleTimeSeconds = parseFloat(costData._originalResult.formulaResult.CycleTime.totalCycleTime_s) || 0;
      }

      // Extract machine tonnage from costData (from backend response or formulaResult)
      let machineTonnageTons = 0;
      if (costData?.machineTonnage_ton !== undefined && costData.machineTonnage_ton !== null) {
        machineTonnageTons = parseFloat(costData.machineTonnage_ton) || 0;
      } else if (costData?.formulaResult?.MachineTonnage?.requiredTonnage_ton !== undefined) {
        machineTonnageTons = parseFloat(costData.formulaResult.MachineTonnage.requiredTonnage_ton) || 0;
      } else if (costData?._originalResult?.machineTonnage_ton !== undefined) {
        machineTonnageTons = parseFloat(costData._originalResult.machineTonnage_ton) || 0;
      } else if (costData?._originalResult?.formulaResult?.MachineTonnage?.requiredTonnage_ton !== undefined) {
        machineTonnageTons = parseFloat(costData._originalResult.formulaResult.MachineTonnage.requiredTonnage_ton) || 0;
      }

      setPlayBookData((prev) => {
        // If playBookData already exists, only update values that are not set
        const existingCavities = prev?.costParams?.cavities;
        const existingOhpRate = prev?.costParams?.ohpRate;
        const existingCycleTime = prev?.costParams?.cycleTime;
        const existingMachineTonnage = prev?.costParams?.machineTonnage;

        return {
          ...(prev || {}),
          costParams: {
            ...(prev?.costParams || {}),
            cavities: existingCavities && existingCavities !== 0
              ? existingCavities
              : (detailsData.noOfCavities !== undefined && detailsData.noOfCavities !== null
                ? parseInt(detailsData.noOfCavities) || 0
                : 0),
            cycleTime: existingCycleTime && existingCycleTime !== 0
              ? existingCycleTime
              : cycleTimeSeconds,
            machineTonnage: existingMachineTonnage && existingMachineTonnage !== 0
              ? existingMachineTonnage
              : (machineTonnageTons > 0
                ? machineTonnageTons
                : (detailsData.machineTonnage !== undefined && detailsData.machineTonnage !== null
                  ? parseFloat(detailsData.machineTonnage) || 0
                  : (prev?.costParams?.machineTonnage || 0))),
            ohpRate: existingOhpRate && existingOhpRate !== 0
              ? existingOhpRate
              : calculatedOhpRate,
            runnerType: detailsData.runnerType || prev?.costParams?.runnerType || "N/A",
          },
        };
      });
    }
  };

  const handleProductComplete = (data) => {
    setProductData(data);
  };

  // Sync playBookData with productData and costData when they change and playbook section is started
  // This ensures the cavity value from productData, OHP rate from costData, and cycle time from costData are set in playBookData
  useEffect(() => {
    if (sectionStarted.playbook && productData) {
      const detailsData = productData.partDetails || {};
      const calculatedOhpRate = costData ? calculateOhpRateFromCostData(costData) : 0;

      // Extract cycle time from costData (from backend response or formulaResult)
      let cycleTimeSeconds = 0;
      if (costData?.cycleTime_s !== undefined && costData.cycleTime_s !== null) {
        cycleTimeSeconds = parseFloat(costData.cycleTime_s) || 0;
      } else if (costData?.formulaResult?.CycleTime?.totalCycleTime_s !== undefined) {
        cycleTimeSeconds = parseFloat(costData.formulaResult.CycleTime.totalCycleTime_s) || 0;
      } else if (costData?._originalResult?.cycleTime_s !== undefined) {
        cycleTimeSeconds = parseFloat(costData._originalResult.cycleTime_s) || 0;
      } else if (costData?._originalResult?.formulaResult?.CycleTime?.totalCycleTime_s !== undefined) {
        cycleTimeSeconds = parseFloat(costData._originalResult.formulaResult.CycleTime.totalCycleTime_s) || 0;
      }

      // Extract machine tonnage from costData (from backend response or formulaResult)
      let machineTonnageTons = 0;
      if (costData?.machineTonnage_ton !== undefined && costData.machineTonnage_ton !== null) {
        machineTonnageTons = parseFloat(costData.machineTonnage_ton) || 0;
      } else if (costData?.formulaResult?.MachineTonnage?.requiredTonnage_ton !== undefined) {
        machineTonnageTons = parseFloat(costData.formulaResult.MachineTonnage.requiredTonnage_ton) || 0;
      } else if (costData?._originalResult?.machineTonnage_ton !== undefined) {
        machineTonnageTons = parseFloat(costData._originalResult.machineTonnage_ton) || 0;
      } else if (costData?._originalResult?.formulaResult?.MachineTonnage?.requiredTonnage_ton !== undefined) {
        machineTonnageTons = parseFloat(costData._originalResult.formulaResult.MachineTonnage.requiredTonnage_ton) || 0;
      }

      setPlayBookData((prev) => {
        // Initialize if playBookData doesn't exist
        if (!prev) {
          return {
            costParams: {
              cavities: detailsData.noOfCavities !== undefined && detailsData.noOfCavities !== null
                ? parseInt(detailsData.noOfCavities) || 0
                : 0,
              cycleTime: cycleTimeSeconds,
              machineTonnage: machineTonnageTons > 0
                ? machineTonnageTons
                : (detailsData.machineTonnage !== undefined && detailsData.machineTonnage !== null
                  ? parseFloat(detailsData.machineTonnage) || 0
                  : 0),
              ohpRate: calculatedOhpRate,
              runnerType: detailsData.runnerType || "N/A",
            },
          };
        }

        const existingCostParams = prev.costParams || {};
        // Only update cavity if it's not set or is 0 (user hasn't manually changed it)
        const shouldUpdateCavity = !existingCostParams.cavities || existingCostParams.cavities === 0;
        const newCavities = detailsData.noOfCavities !== undefined && detailsData.noOfCavities !== null
          ? parseInt(detailsData.noOfCavities) || 0
          : existingCostParams.cavities || 0;

        // Only update cycle time if it's not set or is 0 (user hasn't manually changed it)
        const shouldUpdateCycleTime = !existingCostParams.cycleTime || existingCostParams.cycleTime === 0;

        // Only update machine tonnage if it's not set or is 0 (user hasn't manually changed it)
        const shouldUpdateMachineTonnage = !existingCostParams.machineTonnage || existingCostParams.machineTonnage === 0;

        // Only update OHP rate if it's not set or is 0 (user hasn't manually changed it)
        const shouldUpdateOhpRate = !existingCostParams.ohpRate || existingCostParams.ohpRate === 0;

        return {
          ...prev,
          costParams: {
            ...existingCostParams,
            cavities: shouldUpdateCavity ? newCavities : existingCostParams.cavities,
            cycleTime: shouldUpdateCycleTime ? cycleTimeSeconds : existingCostParams.cycleTime,
            machineTonnage: shouldUpdateMachineTonnage && machineTonnageTons > 0
              ? machineTonnageTons
              : (shouldUpdateMachineTonnage && detailsData.machineTonnage !== undefined && detailsData.machineTonnage !== null
                ? parseFloat(detailsData.machineTonnage) || 0
                : existingCostParams.machineTonnage || 0),
            ohpRate: shouldUpdateOhpRate ? calculatedOhpRate : existingCostParams.ohpRate,
            runnerType: detailsData.runnerType || existingCostParams.runnerType || "N/A",
          },
        };
      });
    }
  }, [productData, costData, sectionStarted.playbook]);

  // Helper function to recalculate OHP cost when OHP Rate changes
  const recalculateOHPCost = (newOhpRate, costData, updatedCostParams) => {
    if (!costData || !costData.breakdown || !costData._originalResult) {
      return costData;
    }

    const to4 = (n) => {
      if (n === null || n === undefined || n === '') return 0;
      if (typeof n === 'string') {
        const cleaned = n.replace(/[$,\s]/g, '').trim();
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
      }
      return Number(Number(n).toFixed(4));
    };

    // Get base material and process costs (these don't change)
    const material = Number(costData.breakdown.material || 0);
    const process = Number(costData.breakdown.process || 0);
    const baseOhpCost = Number(costData.meta?.baseOhpCost ?? costData.breakdown.ohp ?? 0);
    const baseTotal = Number(
      costData.breakdown.total_should_cost ||
      costData.breakdown.total ||
      (material + process + baseOhpCost)
    );

    // Base OHP rate as percentage of total (matching cost category table)
    const baseOhpRate = baseTotal > 0 && baseOhpCost > 0
      ? (baseOhpCost / baseTotal) * 100
      : 0;

    console.log(
      "🧮 Base OHP Rate (as % of total):",
      `${baseOhpRate.toFixed(2)}% = (${baseOhpCost} / ${baseTotal}) * 100`
    );

    // Target OHP rate as percentage of total (matching cost category table)
    const targetOhpRate =
      typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate ?? 0);

    console.log(
      "➡️ Recalculating OHP:",
      `Target rate=${targetOhpRate}% (of total), base rate=${baseOhpRate.toFixed(2)}%`
    );

    // Calculate new OHP based on target percentage of total
    // If target rate is percentage of total: targetRate = (newOHP / (material + process + newOHP)) * 100
    // Solving for newOHP: newOHP = (targetRate / 100) * (material + process) / (1 - targetRate / 100)
    let ohp = baseOhpCost;
    if (!isNaN(targetOhpRate) && targetOhpRate > 0 && targetOhpRate < 100) {
      const baseCost = material + process;
      if (baseCost > 0) {
        const ohpRateDecimal = targetOhpRate / 100;
        const calc = (baseCost * ohpRateDecimal) / (1 - ohpRateDecimal);
        console.log(
          "📐 OHP Calculation (as % of total):",
          `(${baseCost} × ${ohpRateDecimal}) / (1 - ${ohpRateDecimal}) = ${calc}`
        );
        ohp = to4(calc);
      }
    }

    // Calculate new total: material + process + ohp
    const calculatedTotal = Number(material) + Number(process) + Number(ohp);
    const total = to4(calculatedTotal);

    // Update assumptions array with updated PlayBook values
    const resultData = costData._originalResult;
    const assumptionsArray = [];

    // Part Dimensions - from product data
    const dimensionData = productData?.partWeightDimensions || {};
    if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
      assumptionsArray.push(
        `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`
      );
    }

    // Machine Tonnage
    if (updatedCostParams.machineTonnage) {
      assumptionsArray.push(`Machine tonnage: ${updatedCostParams.machineTonnage}`);
    } else if (resultData.Tonnage || resultData.cTonnage) {
      assumptionsArray.push(`Machine tonnage: ${resultData.Tonnage || resultData.cTonnage}`);
    }

    // Cavities
    if (updatedCostParams.cavities) {
      assumptionsArray.push(`Cavities: ${updatedCostParams.cavities}`);
    } else if (resultData.cCavities || resultData.Cavities) {
      assumptionsArray.push(`Cavities: ${resultData.cCavities || resultData.Cavities}`);
    }

    // Runner Type
    if (updatedCostParams.runnerType && updatedCostParams.runnerType !== "N/A") {
      assumptionsArray.push(`Runner type: ${updatedCostParams.runnerType}`);
    } else if (resultData.RunnerType) {
      assumptionsArray.push(`Runner type: ${resultData.RunnerType}`);
    }

    // Cycle Time
    if (updatedCostParams.cycleTime) {
      assumptionsArray.push(`Cycle time: ${updatedCostParams.cycleTime}s`);
    } else if (resultData.CycleTime) {
      assumptionsArray.push(`Cycle time: ${resultData.CycleTime}s`);
    }

    // Machine Rate - always from stored procedure
    if (resultData.MHR) {
      const machineRate = typeof resultData.MHR === 'number' ? resultData.MHR : parseFloat(resultData.MHR);
      if (!isNaN(machineRate)) {
        assumptionsArray.push(`Machine rate: $${machineRate.toFixed(4)}/hour`);
      }
    }

    // Material Price - always from stored procedure
    if (resultData.RMPrice) {
      const materialPrice = typeof resultData.RMPrice === 'number' ? resultData.RMPrice : parseFloat(resultData.RMPrice);
      if (!isNaN(materialPrice)) {
        assumptionsArray.push(`Material price: $${materialPrice.toFixed(2)}/kg`);
      }
    }

    // Labour Cost - always from stored procedure
    if (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) {
      const labourCost = typeof (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost) === 'number'
        ? (resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost)
        : parseFloat(resultData.LaborCost || resultData.LabourCost || resultData.laborCost || resultData.labourCost);
      if (!isNaN(labourCost)) {
        assumptionsArray.push(`Labour cost: $${labourCost.toFixed(4)}/unit`);
      }
    }

    // OHP Rate - use updated value
    if (newOhpRate !== null && newOhpRate !== undefined && newOhpRate !== 0) {
      const ohpRateValue = typeof newOhpRate === 'number' ? newOhpRate : parseFloat(newOhpRate);
      if (!isNaN(ohpRateValue)) {
        assumptionsArray.push(`OHP rate: ${ohpRateValue.toFixed(2)}%`);
      }
    }

    // Update cost data with new OHP, total, and assumptions
    return {
      ...costData,
      breakdown: {
        ...costData.breakdown,
        ohp: ohp,
        total_should_cost: total,
      },
      summary: costData.summary.replace(
        /\$[\d.]+ per unit/,
        `$${total} per unit`
      ),
      assumptions:
        assumptionsArray.length > 0 ? assumptionsArray : costData.assumptions,
      meta: {
        ...costData.meta,
        currentOhpRate: targetOhpRate,
      },
    };
  };

  // Helper function to fetch material price and update material cost when tonnage changes
  const updateMaterialCostFromTonnage = async (tonnage, updatedCostParams) => {
    console.log("\n" + "=".repeat(80));
    console.log("💰 updateMaterialCostFromTonnage called");
    console.log("=".repeat(80));
    console.log("📥 Input Parameters:");
    console.log("   - Tonnage:", tonnage);
    console.log("   - CostData exists:", !!costData);
    console.log("   - CostData source:", costData?._source);
    console.log("   - ProductData exists:", !!productData);

    if (!costData || costData._source !== "stored_procedure" || !productData) {
      console.log("⚠️ Early return: Missing costData or productData");
      return;
    }

    try {
      const manufacturingCountry = productData.manufacturingCountry || "";
      const materialName = productData.materialSelection || null;
      const partWeightG = productData.partWeightDimensions?.finishWeight || 0;

      console.log("📋 Product Data:");
      console.log("   - Manufacturing Country:", manufacturingCountry);
      console.log("   - Material Name:", materialName);
      console.log("   - Part Weight (g):", partWeightG);

      if (!manufacturingCountry || !tonnage || partWeightG <= 0) {
        console.warn("⚠️ Cannot update material cost: missing country, tonnage, or part weight");
        console.log("   - Country:", manufacturingCountry || "MISSING");
        console.log("   - Tonnage:", tonnage || "MISSING");
        console.log("   - Part Weight:", partWeightG || "MISSING");
        return;
      }

      // Get current material cost before update
      const currentMaterialCost = costData.breakdown?.material || 0;
      const currentMaterialPricePerKg = costData.meta?.materialPricePerKg || null;

      console.log("\n📊 Current Values:");
      console.log("   - Current Material Cost:", `$${currentMaterialCost}/unit`);
      console.log("   - Current Material Price:", currentMaterialPricePerKg ? `$${currentMaterialPricePerKg}/kg` : "N/A");

      const requestPayload = {
        tonnage: tonnage,
        manufacturingCountry: manufacturingCountry,
        materialName: materialName,
      };

      console.log("\n📤 API Request Payload:");
      console.log(JSON.stringify(requestPayload, null, 2));
      console.log(`\n🔁 Fetching material price for ${tonnage} tons in ${manufacturingCountry}...`);

      const response = await apiFetch("/api/mech/material-price", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestPayload),
      });

      console.log("📥 API Response Status:", response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to fetch material price" }));
        console.error("❌ API Error Response:", errorData);
        throw new Error(errorData.error || "Failed to fetch material price");
      }

      const result = await response.json();
      console.log("\n📥 API Response Data:");
      console.log(JSON.stringify(result, null, 2));

      const newMaterialPricePerKg = parseFloat(result.material_price || 0);
      console.log("\n💰 Material Price Parsed:");
      console.log("   - Raw value:", result.material_price);
      console.log("   - Parsed value:", newMaterialPricePerKg);

      if (isNaN(newMaterialPricePerKg) || newMaterialPricePerKg <= 0) {
        console.warn("⚠️ Invalid material price received:", result);
        return;
      }

      // Calculate new material cost per unit: (part_weight_g / 1000) * material_price_per_kg
      // const partWeightKg = parseFloat(partWeightG) / 1000;
      // const newMaterialCost = Number((partWeightKg * newMaterialPricePerKg).toFixed(4));
      // Update Material in cost category table with material price per kg directly
      const newMaterialCost = Number(Number(newMaterialPricePerKg).toFixed(4));

      console.log("\n🧮 Calculation:");
      console.log("   - Material Price ($/kg):", newMaterialPricePerKg);
      console.log("   - Using material price per kg directly as material cost");
      console.log("   - New Material Cost ($/unit):", newMaterialCost);

      // Update costData with new material cost
      const to4 = (n) => {
        if (n === null || n === undefined || n === '') return 0;
        if (typeof n === 'string') {
          const cleaned = n.replace(/[$,\s]/g, '').trim();
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
        }
        return Number(Number(n).toFixed(4));
      };

      const process = to4(costData.breakdown.process || 0);
      const ohp = to4(costData.breakdown.ohp || 0);
      const newTotal = Number((newMaterialCost + process + ohp).toFixed(4));

      console.log("\n📊 Cost Breakdown Update:");
      console.log("   - Material (old):", `$${currentMaterialCost}/unit`);
      console.log("   - Material (new):", `$${newMaterialCost}/unit`);
      console.log("   - Process:", `$${process}/unit`);
      console.log("   - OHP:", `$${ohp}/unit`);
      console.log("   - Total (old):", `$${costData.breakdown.total_should_cost}/unit`);
      console.log("   - Total (new):", `$${newTotal}/unit`);
      console.log("   - Total Formula:", `${newMaterialCost} + ${process} + ${ohp} = ${newTotal}`);

      const updatedCostData = {
        ...costData,
        breakdown: {
          ...costData.breakdown,
          material: newMaterialCost,
          total_should_cost: newTotal,
        },
        summary: costData.summary.replace(
          /\$[\d.]+ per unit/,
          `$${newTotal} per unit`
        ),
        assumptions: costData.assumptions.map((assumption) => {
          // Update material price in assumptions if it exists
          if (assumption.includes("Material price:")) {
            return `Material price: $${newMaterialPricePerKg.toFixed(2)}/kg`;
          }
          return assumption;
        }),
        meta: {
          ...costData.meta,
          materialPricePerKg: newMaterialPricePerKg,
        },
      };

      setCostData(updatedCostData);
      console.log("\n✅ Material cost updated successfully!");
      console.log("   - New Material Price:", `$${newMaterialPricePerKg}/kg`);
      console.log("   - New Material Cost:", `$${newMaterialCost}/unit`);
      console.log("   - New Total Cost:", `$${newTotal}/unit`);
      console.log("=".repeat(80) + "\n");
    } catch (error) {
      console.error("\n❌ Failed to update material cost:");
      console.error("   - Error type:", error.constructor.name);
      console.error("   - Error message:", error.message);
      console.error("   - Stack:", error.stack);
      console.log("=".repeat(80) + "\n");
      // Don't show alert to user, just log the error
    }
  };

  const handlePlayBookChange = (field, value) => {
    // If updating costParams, merge it properly
    if (field === "costParams") {
      const previousOhpRate = playBookData?.costParams?.ohpRate;
      const newOhpRate = value.ohpRate;
      const previousTonnage = playBookData?.costParams?.machineTonnage;
      const newTonnage = value.machineTonnage;

      setPlayBookData({
        ...playBookData,
        costParams: value,
      });
      setKeyInsightsData(null);

      // If OHP Rate changed and we have existing costData, recalculate OHP cost
      if (costData && costData._source === "stored_procedure" &&
        newOhpRate !== undefined && newOhpRate !== null &&
        newOhpRate !== previousOhpRate) {
        const updatedCostData = recalculateOHPCost(newOhpRate, costData, value);
        setCostData(updatedCostData);
      }

      // Note: Material price update is now handled when Quick Cost button is clicked
      // No automatic update here - wait for user to click Quick Cost button
    } else {
      setPlayBookData({ ...playBookData, [field]: value });
    }
  };

  // Helper function to analyze cost with explicit data
  const analyzeCostWithData = async (recipe, playbook, product) => {
    try {
      console.log("🚀 Analyzing cost, risk, and substitutes with:", { recipe, playbook, product });

      const payload = {
        recipe: recipe,
        playBookData: playbook,
        productData: product,
      };

      // Call all three APIs in parallel
      const [costResponse, riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/fnb/analyze-cost", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/analyze-risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/fnb/find-substitutes", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      // Check responses
      if (!costResponse.ok) {
        const errorData = await costResponse
          .json()
          .catch(() => ({ error: "Cost analysis failed" }));
        throw new Error(errorData.error || "Failed to analyze cost");
      }

      // Parse all responses (non-blocking for risk and substitute)
      const costResult = await costResponse.json();
      console.log("✅ Cost analysis complete:", costResult);
      setCostData(costResult);

      // Handle risk analysis
      if (riskResponse.ok) {
        const riskResult = await riskResponse.json();
        console.log("✅ Risk analysis complete:", riskResult);
        setRiskData(riskResult);
      } else {
        console.warn("⚠️ Risk analysis failed, continuing without risk data");
        setRiskData(null);
      }

      // Handle substitute analysis
      if (substituteResponse.ok) {
        const substituteResult = await substituteResponse.json();
        console.log("✅ Substitute analysis complete:", substituteResult);
        setSubstituteData(substituteResult);
      } else {
        console.warn("⚠️ Substitute analysis failed, continuing without substitute data");
        setSubstituteData(null);
      }

      // Trigger image generation (non-blocking)
      generateProductImageWithData(product, playbook);

      // Navigate to results section and mark as started
      setActiveSection("results");
      setSectionStarted((prev) => ({ ...prev, results: true }));

      return costResult;
    } catch (error) {
      console.error("Analysis failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      throw error;
    }
  };

  const handleAnalyzeCost = async () => {
    if (!recipeData || !playBookData || !productData) return;

    setLoadingMessage("Running cost analysis...");
    setIsProcessing(true);
    try {
      await analyzeCostWithData(recipeData, playBookData, productData);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  // Fetch risk and substitute analysis for Analysis Report tabs.
  const fetchRiskAndSubstituteData = async (product, playbook) => {
    if (!product) {
      setRiskData(null);
      setSubstituteData(null);
      return;
    }

    const payload = {
      towerName: "Mechanical",
      costModel: costData?.breakdown || {},
      form: {
        productData: product || {},
        playBookData: playbook || {},
        recipe: recipeData || [],
      },
    };

    const fallbackRecipe = Array.isArray(recipeData) && recipeData.length > 0
      ? recipeData
      : [
        {
          ingredient: product?.materialSelection || "Base Resin",
          percentage: 100,
          functional_group: "Base",
        },
      ];

    const fnbPayload = {
      recipe: fallbackRecipe,
      playBookData: playbook || {},
      productData: product || {},
    };

    const buildFallbackRisk = () => ({
      summary: `Risk snapshot for ${product?.productName || "plastic part"} based on available costing inputs.`,
      top_concerns: [
        "Raw material price volatility can affect unit economics.",
        "Cycle time drift can increase process cost.",
        "Machine utilization and downtime may impact output consistency.",
      ],
      risk_items: [
        {
          title: "Material Price Volatility",
          severity: 6,
          probability: 6,
          detail: "Track resin index and lock supplier rates where possible.",
        },
        {
          title: "Cycle Time Variability",
          severity: 5,
          probability: 5,
          detail: "Validate mold/process window and monitor actual cycle data.",
        },
      ],
      assumptions: "Generated from plastics product and playbook inputs (fallback mode).",
    });

    const buildFallbackSubstitute = () => {
      const details = product?.partDetails || {};
      const params = playbook?.costParams || {};
      const material = product?.materialSelection || "Current resin grade";
      const runnerType = params.runnerType || details.runnerType || "Current runner setup";
      const cavities = params.cavities ?? details.noOfCavities ?? "Current cavities";
      const cycleTime = params.cycleTime || "Current cycle time";
      const machineTonnage = params.machineTonnage || details.machineTonnage || "Current tonnage";

      return {
        summary: `Plastics molding optimization recommendations generated for ${product?.productName || "the current part"} using selected material and process inputs.`,
        substitutes: [
          {
            current_item: `Material: ${material}`,
            proposed_item: "Validate a cost-optimized equivalent resin grade",
            estimated_cost_delta_per_unit: "Potential material cost reduction",
            impact_on_quality: "Requires validation",
            feasibility_score: 6,
            timeline_weeks: 4,
            risk_note: "Check shrinkage, strength, and thermal behavior before release.",
            validation_steps: ["Material datasheet comparison", "Mold trial run", "Part qualification"],
          },
          {
            current_item: `Runner setup: ${runnerType}`,
            proposed_item: "Evaluate runner/gate optimization for lower scrap and cycle loss",
            estimated_cost_delta_per_unit: "Potential process cost reduction",
            impact_on_quality: "Low to medium",
            feasibility_score: 7,
            timeline_weeks: 3,
            risk_note: "Validate fill balance and cosmetic quality.",
            validation_steps: ["Moldflow review", "Pilot run", "Scrap trend check"],
          },
          {
            current_item: `Cycle time: ${cycleTime}, cavities: ${cavities}, tonnage: ${machineTonnage}`,
            proposed_item: "Tune cycle and clamp settings to improve throughput",
            estimated_cost_delta_per_unit: "Potential conversion cost reduction",
            impact_on_quality: "Process-dependent",
            feasibility_score: 7,
            timeline_weeks: 2,
            risk_note: "Monitor warpage and dimensional capability during tuning.",
            validation_steps: ["DOE on cycle settings", "Capability study", "Standardize process window"],
          },
        ],
        prioritization: [
          "Process tuning (cycle/clamp) for immediate savings",
          "Runner/gate optimization to reduce scrap",
          "Material grade optimization after process stabilization",
        ],
        assumptions: "Generated from plastics product and playbook inputs (fallback mode).",
      };
    };

    try {
      let [riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/ai/costing-v4/plastics/risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/ai/costing-v4/plastics/substitute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      // Fallback to FNB endpoints if costing-v4 fails.
      if (!riskResponse.ok || !substituteResponse.ok) {
        console.warn("costing-v4 risk/substitute failed, falling back to /api/fnb endpoints");
        [riskResponse, substituteResponse] = await Promise.all([
          apiFetch("/api/fnb/analyze-risk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
          apiFetch("/api/fnb/find-substitutes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
        ]);
      }

      if (riskResponse.ok) {
        const riskResult = await riskResponse.json();
        setRiskData(riskResult);
      } else {
        const riskErr = await riskResponse.json().catch(() => ({}));
        console.warn("Risk analysis failed:", riskErr);
        setRiskData(buildFallbackRisk());
      }

      if (substituteResponse.ok) {
        const substituteResult = await substituteResponse.json();
        setSubstituteData(substituteResult);
      } else {
        const substituteErr = await substituteResponse.json().catch(() => ({}));
        console.warn("Substitute analysis failed:", substituteErr);
        setSubstituteData(buildFallbackSubstitute());
      }
    } catch (error) {
      console.error("Risk/Substitute analysis failed:", error);
      setRiskData(buildFallbackRisk());
      setSubstituteData(buildFallbackSubstitute());
    }
  };

  // Commented out due to permission error with DALL-E-3 model
  // const generateProductImageWithData = async (product, playbook) => {
  //   if (!product) {
  //     console.warn("⚠️ Cannot generate image: product data is missing");
  //     return;
  //   }

  //   setGeneratingImage(true);

  //   try {
  //     // Build payload with product information for plastics-specific image generation
  //     const imagePayload = {
  //       productName: product.productName || "",
  //       materialSelection: product.materialSelection || "",
  //       partWeightDimensions: product.partWeightDimensions || {},
  //     };

  //     // Validate that we have at least product name
  //     if (!imagePayload.productName) {
  //       console.warn("⚠️ Cannot generate image: product name is missing");
  //       setGeneratingImage(false);
  //       return;
  //     }

  //     console.log("🖼️ Generating plastics product image:", imagePayload);
  //     console.log("   - Product Name:", imagePayload.productName);
  //     console.log("   - Material:", imagePayload.materialSelection);
  //     console.log("   - Dimensions:", imagePayload.partWeightDimensions);

  //     const response = await apiFetch("/api/mech/plastics/generate-image", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(imagePayload),
  //     });

  //     if (!response.ok) {
  //       const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
  //       console.error("❌ Image generation failed:", errorData);
  //       throw new Error(errorData.error || "Failed to generate image");
  //     }

  //     const data = await response.json();
  //     if (data.image_url) {
  //       setProductImageUrl(data.image_url);
  //       console.log("✅ Product image generated successfully");
  //       console.log("   Image URL:", data.image_url);
  //       if (data.prompt_used) {
  //         console.log("📝 Prompt used:", data.prompt_used.substring(0, 200) + "...");
  //       }
  //     } else {
  //       console.warn("⚠️ Image generation response missing image_url");
  //     }
  //   } catch (error) {
  //     console.error("❌ Error generating product image:", error);
  //     console.error("   Error details:", error.message);
  //     // Don't alert - image is optional, but log for debugging
  //   } finally {
  //     setGeneratingImage(false);
  //   }
  // };

  // Commented out due to permission error with DALL-E-3 model
  // const generateProductImage = () => {
  //   return generateProductImageWithData(productData, playBookData);
  // };

  const generateProductImageWithData = async (product, playbook) => {
    if (!product) {
      console.warn("âš ï¸ Cannot generate image: product data is missing");
      return;
    }

    setGeneratingImage(true);

    try {
      // Build payload with product information for plastics-specific image generation
      const imagePayload = {
        productName: product.productName || "",
        materialSelection:
          product.materialSelection?.material ||
          product.materialSelection?.name ||
          product.materialSelection ||
          "",
        partWeightDimensions: product.partWeightDimensions || {},
      };

      // Validate that we have at least product name
      if (!imagePayload.productName) {
        console.warn("âš ï¸ Cannot generate image: product name is missing");
        setGeneratingImage(false);
        return;
      }

      console.log("ðŸ–¼ï¸ Generating plastics product image:", imagePayload);
      console.log("   - Product Name:", imagePayload.productName);
      console.log("   - Material:", imagePayload.materialSelection);
      console.log("   - Dimensions:", imagePayload.partWeightDimensions);

      const response = await apiFetch("/api/mech/plastics/generate-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
        console.error("âŒ Image generation failed:", errorData);
        throw new Error(errorData.error || "Failed to generate image");
      }

      const data = await response.json();
      if (data.image_url) {
        setProductImageUrl(data.image_url);
        console.log("âœ… Product image generated successfully");
        console.log("   Image URL:", data.image_url);
        if (data.prompt_used) {
          console.log("ðŸ“ Prompt used:", data.prompt_used.substring(0, 200) + "...");
        }
      } else {
        console.warn("âš ï¸ Image generation response missing image_url");
      }
    } catch (error) {
      console.error("âŒ Error generating product image:", error);
      console.error("   Error details:", error.message);
      // Don't alert - image is optional, but log for debugging
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    return generateProductImageWithData(productData, playBookData);
  };

  const fetchKeyInsights = async (product, playbook) => {
    if (!product) {
      return;
    }
    try {
      const response = await apiFetch("/api/mech/key-insights", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productData: product,
          playBookData: playbook || {},
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate key insights");
      }
      const insights = await response.json();
      setKeyInsightsData(insights);
      console.log(insights);
    } catch (error) {
      console.error("Failed to fetch key insights:", error);
    }
  };

  // Call plastics costing calculation with SSE streaming
  const callPlasticsCostingStream = async (productData, playBookData) => {
    try {
      const timestamp = Date.now();

      // Prepare request payload matching backend expectations
      const requestData = {
        productData: productData,  // Send full productData object
        projectId: `PROJ_${timestamp}`,
        partKey: `P1`,
        // Include playbook if available
        ...(playBookData && { existingPlaybook: playBookData }),
      };

      console.log("📞 Calling plastics streaming endpoint:", requestData);

      // Call SSE streaming endpoint
      const response = await apiFetch("/api/plastics/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete events (separated by \n\n)
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // Keep incomplete event in buffer

        for (const event of events) {
          if (!event.trim()) continue;

          // Extract JSON from "data: {...}" format
          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const eventData = JSON.parse(dataMatch[1]);
            console.log("📥 SSE Event:", eventData);

            if (eventData.type === "complete") {
              // Analysis complete - return the final response
              console.log("✅ Analysis complete:", eventData);
              return eventData;
            } else if (eventData.type === "error") {
              throw new Error(eventData.message || "Analysis failed");
            }
            // Ignore progress events for now (can add progress tracking later)
          } catch (parseErr) {
            console.warn("Failed to parse SSE event:", parseErr);
          }
        }
      }

      throw new Error("Stream ended without complete event");
    } catch (err) {
      console.error("❌ Plastics streaming call failed:", err);
      throw err;
    }
  };

  // Call stored procedure QC_SP_Plastics
  const callStoredProcedure = async (productData, playBookData) => {
    try {
      const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
      const userIdentifier =
        user?.email || user?.username || fullName || "User";

      // Extract dimension data from productData
      const dimensionData = productData.partWeightDimensions || {};

      // Extract details data from productData
      const detailsData = productData.partDetails || {};

      // Get Batch size from productData
      const batchSizeValue = productData.batchSize || "";
      let batchSize = 0;
      if (batchSizeValue) {
        if (typeof batchSizeValue === 'number') {
          batchSize = batchSizeValue;
        } else if (typeof batchSizeValue === 'string') {
          const parsed = parseFloat(batchSizeValue);
          if (!isNaN(parsed)) {
            batchSize = parsed;
          }
        }
      }

      // Get costParams from playBookData
      const costParams = playBookData?.costParams || {};

      // Prepare stored procedure parameters
      const spParams = {
        ID: 0, // Default 0
        PartName: productData.productName || "",
        MfrCountry: productData.manufacturingCountry || "",
        BatchSize: batchSize,
        MaterialName: productData.materialSelection || "",
        PartLength: dimensionData.partLength || "",
        PartWidth: dimensionData.partWidth || "",
        PartHeight: dimensionData.partHeight || "",
        PartWeight: dimensionData.finishWeight || "",
        AvgWallThick: detailsData.avgWallThickness || "",
        Finish: "2025-Q2A", // Hardcoded
        RunnerType: detailsData.runnerType || costParams.runnerType || "N/A",
        Regrind: detailsData.regrindPct || "",
        Cavities: detailsData.noOfCavities || costParams.cavities || 0,
        Tonnage: detailsData.machineTonnage || costParams.machineTonnage || 0,
        CreatedBy: userIdentifier,
        userEmail: user?.email || "",
        matPrice: costParams.matPrice || 0,
        GedbQtr: "2025-Q2A", // Hardcoded
      };

      console.log("📞 Calling stored procedure QC_SP_Plastics with params:", spParams);

      // Call the stored procedure API endpoint
      const response = await apiFetch("/api/costing/plastics", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(spParams),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Stored procedure call failed (${response.status})`);
      }

      const result = await response.json();
      console.log("✅ Stored procedure result:", result);
      return result;
    } catch (err) {
      console.error("❌ Stored procedure call failed:", err);
      throw err;
    }
  };

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    console.log("Starting Progressive Quick Cost Analysis with SSE...");

    // Initialize progress steps immediately with descriptive labels
    const totalSteps = 6;
    const initialSteps = [
      { id: "step-1", label: "Documenting Assumptions", status: "in-progress", message: "Processing..." },
      { id: "step-2", label: "Analyzing Material Requirements", status: "pending", message: "" },
      { id: "step-3", label: "Calculating Process Costs", status: "pending", message: "" },
      { id: "step-4", label: "Computing Machine Parameters", status: "pending", message: "" },
      { id: "step-5", label: "Generating Cost Breakdown", status: "pending", message: "" },
      { id: "step-6", label: "Finalizing Analysis Report", status: "pending", message: "" },
    ];
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);
    setRiskData(null);
    setSubstituteData(null);

    // Track maximum progress to prevent going backwards
    let maxProgress = 0;

    try {
      const timestamp = Date.now();

      // Prepare request payload matching backend expectations
      const requestData = {
        productData: productData,  // Send full productData object
        userEmail: user?.email || "",
        projectId: `PROJ_${timestamp}`,
        partKey: `P1`,
        // Include playbook if available
        ...(playBookData && { existingPlaybook: playBookData }),
      };

      console.log("📞 Calling plastics streaming endpoint:", requestData);

      // Call SSE streaming endpoint
      const response = await apiFetch("/api/plastics/execute-complete-analysis-stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete events (separated by \n\n)
        const events = buffer.split("\n\n");
        buffer = events.pop() || ""; // Keep incomplete event in buffer

        for (const event of events) {
          if (!event.trim()) continue;

          // Extract JSON from "data: {...}" format
          const dataMatch = event.match(/^data: (.+)$/m);
          if (!dataMatch) continue;

          try {
            const eventData = JSON.parse(dataMatch[1]);
            console.log("📥 SSE Event:", eventData);

            if (eventData.type === "start") {
              // Update total steps if different from initial estimate
              const totalSteps = eventData.total_steps || 6;
              if (totalSteps !== progressSteps.length) {
                // Generate descriptive step labels
                const stepLabels = [
                  "Documenting Assumptions",
                  "Analyzing Material Requirements",
                  "Calculating Process Costs",
                  "Computing Machine Parameters",
                  "Generating Cost Breakdown",
                  "Finalizing Analysis Report",
                ];
                const updatedSteps = Array.from({ length: totalSteps }, (_, i) => ({
                  id: `step-${i + 1}`,
                  label: stepLabels[i] || `Step ${i + 1}`,
                  status: "pending",
                  message: "",
                }));
                setProgressSteps(updatedSteps);
              }
            } else if (eventData.type === "progress") {
              // Update specific step status
              const stepIndex = eventData.step - 1;
              console.log(
                `📍 Progress Event - Step ${eventData.step}: ${eventData.label} (${eventData.status})`
              );

              setProgressSteps((prev) => {
                const updated = [...prev];
                if (updated[stepIndex]) {
                  // Map backend status to frontend status
                  let mappedStatus = eventData.status;
                  if (eventData.status === "running") {
                    mappedStatus = "in-progress";
                  } else if (eventData.status === "success") {
                    mappedStatus = "completed";
                  }

                  // Preserve our descriptive label if backend doesn't provide one
                  const label = eventData.label && eventData.label !== `Step ${eventData.step}`
                    ? eventData.label
                    : updated[stepIndex].label;

                  updated[stepIndex] = {
                    ...updated[stepIndex],
                    label: label,
                    status: mappedStatus,
                    message:
                      eventData.message ||
                      (eventData.status === "running" ? "Processing..." : "Complete"),
                    error: eventData.error,
                  };

                  console.log(
                    `   ✓ Updated step ${eventData.step}: label="${updated[stepIndex].label}", status="${updated[stepIndex].status}"`
                  );
                }
                return updated;
              });

              // Update current step based on the most recent event
              if (eventData.status === "running" || eventData.status === "success") {
                setCurrentProgressStep(eventData.step);
              }

              const newProgress = (eventData.step / eventData.total) * 100;
              if (newProgress > maxProgress) {
                maxProgress = newProgress;
                setOverallProgress(newProgress);
              }
            } else if (eventData.type === "complete") {
              // Analysis complete - populate all state from final response
              console.log("✅ Analysis complete:", eventData);
              console.log("🔍 Complete event keys:", Object.keys(eventData));

              // Set progress to 100%
              maxProgress = 100;
              setOverallProgress(100);

              // Mark all steps as completed
              setProgressSteps((prev) =>
                prev.map((step) => ({
                  ...step,
                  status: "completed",
                  message: "Complete",
                }))
              );

              // Handle the response from streaming endpoint
              if (eventData && eventData.status === 'success') {
                // Extract should cost result
                const shouldCost = eventData.ShouldCostResult || {};
                const material = parseFloat(shouldCost.MaterialCost || 0);
                const process = parseFloat(shouldCost.ProcessCost || 0);
                const ohp = parseFloat(shouldCost.OHPCost || 0);
                const total = parseFloat(shouldCost.TotalCost || 0);

                // Build assumptions array from response
                let assumptionsArray = [];
                if (eventData.AssumptionsJson) {
                  const assumptions = eventData.AssumptionsJson;
                  if (Array.isArray(assumptions)) {
                    assumptionsArray = assumptions;
                  } else if (typeof assumptions === 'object') {
                    Object.values(assumptions).forEach((section) => {
                      if (Array.isArray(section)) {
                        assumptionsArray.push(...section);
                      }
                    });
                  }
                }

                // Extract material price, cycle time, machine tonnage, machine rate
                let materialPricePerKg = null;
                if (eventData.materialPrice_usd_per_kg !== undefined && eventData.materialPrice_usd_per_kg !== null) {
                  materialPricePerKg = parseFloat(eventData.materialPrice_usd_per_kg) || null;
                } else if (eventData.AssumptionsJson?.MaterialMaster?.price_usd_per_kg !== undefined) {
                  materialPricePerKg = parseFloat(eventData.AssumptionsJson.MaterialMaster.price_usd_per_kg) || null;
                } else if (eventData.MaterialMaster?.price_usd_per_kg !== undefined) {
                  materialPricePerKg = parseFloat(eventData.MaterialMaster.price_usd_per_kg) || null;
                }

                if (materialPricePerKg !== null && !isNaN(materialPricePerKg) && materialPricePerKg > 0) {
                  const hasMaterialPrice = assumptionsArray.some(assumption => {
                    const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                    return assumptionStr.toLowerCase().includes('material price:');
                  });

                  if (!hasMaterialPrice) {
                    assumptionsArray.push(`Material price: $${materialPricePerKg.toFixed(2)}/kg`);
                  }
                }

                let cycleTimeSeconds = 0;
                if (eventData.cycleTime_s !== undefined && eventData.cycleTime_s !== null) {
                  cycleTimeSeconds = parseFloat(eventData.cycleTime_s) || 0;
                } else if (eventData.formulaResult?.CycleTime?.totalCycleTime_s !== undefined) {
                  cycleTimeSeconds = parseFloat(eventData.formulaResult.CycleTime.totalCycleTime_s) || 0;
                } else if (eventData.AssumptionsJson?.CycleTime?.totalCycleTime_s !== undefined) {
                  cycleTimeSeconds = parseFloat(eventData.AssumptionsJson.CycleTime.totalCycleTime_s) || 0;
                }

                if (cycleTimeSeconds > 0) {
                  const hasCycleTime = assumptionsArray.some(assumption => {
                    const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                    return assumptionStr.toLowerCase().includes('cycle time') || assumptionStr.toLowerCase().includes('cycle time(sec):');
                  });

                  if (!hasCycleTime) {
                    assumptionsArray.push(`Cycle Time(sec): ${cycleTimeSeconds.toFixed(2)}`);
                  }
                }

                let machineRatePerHour = null;
                if (eventData.machineRate_usd_per_hr !== undefined && eventData.machineRate_usd_per_hr !== null) {
                  machineRatePerHour = parseFloat(eventData.machineRate_usd_per_hr) || null;
                } else if (eventData.AssumptionsJson?.CostingAssumptions?.machineRate_usd_per_hr !== undefined) {
                  machineRatePerHour = parseFloat(eventData.AssumptionsJson.CostingAssumptions.machineRate_usd_per_hr) || null;
                } else if (eventData.CostingAssumptions?.machineRate_usd_per_hr !== undefined) {
                  machineRatePerHour = parseFloat(eventData.CostingAssumptions.machineRate_usd_per_hr) || null;
                }

                if (machineRatePerHour !== null && !isNaN(machineRatePerHour) && machineRatePerHour > 0) {
                  const hasMachineRate = assumptionsArray.some(assumption => {
                    const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
                    return assumptionStr.toLowerCase().includes('machine rate:');
                  });

                  if (!hasMachineRate) {
                    assumptionsArray.push(`Machine rate: $${machineRatePerHour.toFixed(2)}/hour`);
                  }
                }

                if (assumptionsArray.length === 0 && productData) {
                  const dimensionData = productData?.partWeightDimensions || {};
                  if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
                    assumptionsArray.push(
                      `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`
                    );
                  }
                  if (productData.materialSelection) {
                    assumptionsArray.push(`Material: ${productData.materialSelection}`);
                  }
                  if (productData.batchSize) {
                    assumptionsArray.push(`Batch Size: ${productData.batchSize}`);
                  }
                }

                let machineTonnageTons = 0;
                if (eventData.machineTonnage_ton !== undefined && eventData.machineTonnage_ton !== null) {
                  machineTonnageTons = parseFloat(eventData.machineTonnage_ton) || 0;
                } else if (eventData.formulaResult?.MachineTonnage?.requiredTonnage_ton !== undefined) {
                  machineTonnageTons = parseFloat(eventData.formulaResult.MachineTonnage.requiredTonnage_ton) || 0;
                } else if (eventData.AssumptionsJson?.MachineTonnage?.requiredTonnage_ton !== undefined) {
                  machineTonnageTons = parseFloat(eventData.AssumptionsJson.MachineTonnage.requiredTonnage_ton) || 0;
                }

                // Transform to format expected by ResultsSection
                const transformedCostData = {
                  summary: `Should cost analysis for ${productData.productName || "plastic part"} manufactured in ${productData.manufacturingCountry || "location"}. Total cost: $${total.toFixed(4)} per unit.`,
                  breakdown: {
                    material: material,
                    process: process,
                    logistics: 0,
                    ohp: ohp,
                    total_should_cost: total,
                    currency: "USD/unit",
                  },
                  line_items: [],
                  key_insights: [],
                  assumptions: assumptionsArray,
                  _source: "plastics_routes",
                  _originalResult: eventData,
                  formulaResult: eventData.formulaResult,
                  cycleTime_s: cycleTimeSeconds,
                  machineTonnage_ton: machineTonnageTons,
                  materialPrice_usd_per_kg: materialPricePerKg,
                };

                setCostData(transformedCostData);

                // Initialize playBookData with cycle time and machine tonnage
                if (cycleTimeSeconds > 0 || machineTonnageTons > 0) {
                  setPlayBookData((prev) => {
                    const existingCostParams = prev?.costParams || {};
                    const shouldUpdateCycleTime = !existingCostParams.cycleTime || existingCostParams.cycleTime === 0;
                    const shouldUpdateMachineTonnage = !existingCostParams.machineTonnage || existingCostParams.machineTonnage === 0;

                    return {
                      ...(prev || {}),
                      costParams: {
                        ...existingCostParams,
                        cycleTime: shouldUpdateCycleTime ? cycleTimeSeconds : existingCostParams.cycleTime,
                        machineTonnage: shouldUpdateMachineTonnage ? machineTonnageTons : existingCostParams.machineTonnage,
                      },
                    };
                  });
                }

                // Close dialog immediately and navigate to results
                setShowProgressTracker(false);
                setIsProcessing(false);
                setActiveSection("results");
                setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

                // Extract keyInsights from response with normalization
                if (eventData.keyInsights) {
                  console.log("🔑 Raw key insights data:", eventData.keyInsights);
                  // Normalize the insights to ensure proper array structure
                  const normalizedInsights = {
                    summary: eventData.keyInsights.summary || "",
                    cost_savings: Array.isArray(eventData.keyInsights.cost_savings)
                      ? eventData.keyInsights.cost_savings
                      : (eventData.keyInsights.cost_savings ? [eventData.keyInsights.cost_savings] : []),
                    strategic: Array.isArray(eventData.keyInsights.strategic)
                      ? eventData.keyInsights.strategic
                      : (eventData.keyInsights.strategic ? [eventData.keyInsights.strategic] : []),
                  };
                  console.log("📊 Normalized key insights:", normalizedInsights);
                  console.log("   - Cost savings count:", normalizedInsights.cost_savings.length);
                  console.log("   - Strategic count:", normalizedInsights.strategic.length);
                  setKeyInsightsData(normalizedInsights);
                } else if (eventData.KeyInsightsJson) {
                  const unwrappedKeyInsights =
                    eventData.KeyInsightsJson.key_insights || eventData.KeyInsightsJson;
                  console.log("🔑 Raw key insights data (from KeyInsightsJson):", unwrappedKeyInsights);
                  // Normalize the insights to ensure proper array structure
                  const normalizedInsights = {
                    summary: unwrappedKeyInsights.summary || "",
                    cost_savings: Array.isArray(unwrappedKeyInsights.cost_savings)
                      ? unwrappedKeyInsights.cost_savings
                      : (unwrappedKeyInsights.cost_savings ? [unwrappedKeyInsights.cost_savings] : []),
                    strategic: Array.isArray(unwrappedKeyInsights.strategic)
                      ? unwrappedKeyInsights.strategic
                      : (unwrappedKeyInsights.strategic ? [unwrappedKeyInsights.strategic] : []),
                  };
                  console.log("📊 Normalized key insights:", normalizedInsights);
                  console.log("   - Cost savings count:", normalizedInsights.cost_savings.length);
                  console.log("   - Strategic count:", normalizedInsights.strategic.length);
                  setKeyInsightsData(normalizedInsights);
                }

                // Trigger image generation and risk/substitute analysis after Quick Cost completes (non-blocking)
                generateProductImageWithData(productData, playBookData);
                fetchRiskAndSubstituteData(productData, playBookData);
              } else {
                throw new Error(eventData.message || "Plastics costing failed");
              }

              break;
            } else if (eventData.type === "error") {
              throw new Error(eventData.error || eventData.message || "Analysis failed");
            }
          } catch (parseError) {
            console.error("❌ Error parsing SSE event:", parseError);
          }
        }
      }

      console.log("✅ Progressive Quick Cost completed successfully!");
    } catch (error) {
      console.error("❌ Progressive Quick Cost failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setIsProcessing(false);
      setShowProgressTracker(false);
    }
  };

  const handleQuickCost = async () => {
    const Ai_Maintenance = "Schema Management V2";

    if (Ai_Maintenance === "Schema Management V2") {
      handleProgressiveQuickCost();
      return;
    }

    if (!productData) return;

    setLoadingMessage("Generating Quick Cost...");
    setIsProcessing(true);
    setKeyInsightsData(null);

    try {
      // Call plastics costing calculation with streaming
      console.log("📞 Calling plastics costing with streaming:", { productData, playBookData });

      const costResult = await callPlasticsCostingStream(productData, playBookData);

      // Handle the response from streaming endpoint
      if (costResult && costResult.status === 'success') {
        console.log("✅ Plastics costing completed:", costResult);

        // Extract should cost result
        const shouldCost = costResult.ShouldCostResult || {};
        const material = parseFloat(shouldCost.MaterialCost || 0);
        const process = parseFloat(shouldCost.ProcessCost || 0);
        const ohp = parseFloat(shouldCost.OHPCost || 0);
        const total = parseFloat(shouldCost.TotalCost || 0);

        // Build assumptions array from response
        let assumptionsArray = [];
        if (costResult.AssumptionsJson) {
          // Extract assumptions from AssumptionsJson if available
          const assumptions = costResult.AssumptionsJson;
          if (Array.isArray(assumptions)) {
            assumptionsArray = assumptions;
          } else if (typeof assumptions === 'object') {
            // Flatten assumptions object into array
            Object.values(assumptions).forEach((section) => {
              if (Array.isArray(section)) {
                assumptionsArray.push(...section);
              }
            });
          }
        }

        // Extract material price from costResult (from backend response)
        // Check multiple locations: materialPrice_usd_per_kg, AssumptionsJson.MaterialMaster.price_usd_per_kg, or MaterialMaster.price_usd_per_kg
        let materialPricePerKg = null;
        if (costResult.materialPrice_usd_per_kg !== undefined && costResult.materialPrice_usd_per_kg !== null) {
          materialPricePerKg = parseFloat(costResult.materialPrice_usd_per_kg) || null;
        } else if (costResult.AssumptionsJson?.MaterialMaster?.price_usd_per_kg !== undefined) {
          materialPricePerKg = parseFloat(costResult.AssumptionsJson.MaterialMaster.price_usd_per_kg) || null;
        } else if (costResult.MaterialMaster?.price_usd_per_kg !== undefined) {
          materialPricePerKg = parseFloat(costResult.MaterialMaster.price_usd_per_kg) || null;
        }

        // Add material price to assumptions if available
        if (materialPricePerKg !== null && !isNaN(materialPricePerKg) && materialPricePerKg > 0) {
          // Check if material price already exists in assumptions array
          const hasMaterialPrice = assumptionsArray.some(assumption => {
            const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
            return assumptionStr.toLowerCase().includes('material price:');
          });

          if (!hasMaterialPrice) {
            assumptionsArray.push(`Material price: $${materialPricePerKg.toFixed(2)}/kg`);
          }
        }

        // Extract cycle time from costResult (from backend response)
        // Check multiple locations: cycleTime_s, formulaResult.CycleTime.totalCycleTime_s, or AssumptionsJson.CycleTime.totalCycleTime_s
        let cycleTimeSeconds = 0;
        if (costResult.cycleTime_s !== undefined && costResult.cycleTime_s !== null) {
          cycleTimeSeconds = parseFloat(costResult.cycleTime_s) || 0;
        } else if (costResult.formulaResult?.CycleTime?.totalCycleTime_s !== undefined) {
          cycleTimeSeconds = parseFloat(costResult.formulaResult.CycleTime.totalCycleTime_s) || 0;
        } else if (costResult.AssumptionsJson?.CycleTime?.totalCycleTime_s !== undefined) {
          cycleTimeSeconds = parseFloat(costResult.AssumptionsJson.CycleTime.totalCycleTime_s) || 0;
        }

        // Add cycle time to assumptions if available
        if (cycleTimeSeconds > 0) {
          // Check if cycle time already exists in assumptions array
          const hasCycleTime = assumptionsArray.some(assumption => {
            const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
            return assumptionStr.toLowerCase().includes('cycle time') || assumptionStr.toLowerCase().includes('cycle time(sec):');
          });

          if (!hasCycleTime) {
            assumptionsArray.push(`Cycle Time(sec): ${cycleTimeSeconds.toFixed(2)}`);
          }
        }

        // Extract machine rate from costResult (from backend response)
        // Check multiple locations: machineRate_usd_per_hr, AssumptionsJson.CostingAssumptions.machineRate_usd_per_hr, or CostingAssumptions.machineRate_usd_per_hr
        let machineRatePerHour = null;
        if (costResult.machineRate_usd_per_hr !== undefined && costResult.machineRate_usd_per_hr !== null) {
          machineRatePerHour = parseFloat(costResult.machineRate_usd_per_hr) || null;
        } else if (costResult.AssumptionsJson?.CostingAssumptions?.machineRate_usd_per_hr !== undefined) {
          machineRatePerHour = parseFloat(costResult.AssumptionsJson.CostingAssumptions.machineRate_usd_per_hr) || null;
        } else if (costResult.CostingAssumptions?.machineRate_usd_per_hr !== undefined) {
          machineRatePerHour = parseFloat(costResult.CostingAssumptions.machineRate_usd_per_hr) || null;
        }

        // Add machine rate to assumptions if available
        if (machineRatePerHour !== null && !isNaN(machineRatePerHour) && machineRatePerHour > 0) {
          // Check if machine rate already exists in assumptions array
          const hasMachineRate = assumptionsArray.some(assumption => {
            const assumptionStr = typeof assumption === 'string' ? assumption : String(assumption || '');
            return assumptionStr.toLowerCase().includes('machine rate:');
          });

          if (!hasMachineRate) {
            assumptionsArray.push(`Machine rate: $${machineRatePerHour.toFixed(2)}/hour`);
          }
        }

        // Build assumptions from product data if not available
        if (assumptionsArray.length === 0 && productData) {
          const dimensionData = productData?.partWeightDimensions || {};
          if (dimensionData.partLength && dimensionData.partWidth && dimensionData.partHeight && dimensionData.finishWeight) {
            assumptionsArray.push(
              `Part dimensions: L: ${dimensionData.partLength}mm, W: ${dimensionData.partWidth}mm, H: ${dimensionData.partHeight}mm, Weight: ${dimensionData.finishWeight}g`
            );
          }
          if (productData.materialSelection) {
            assumptionsArray.push(`Material: ${productData.materialSelection}`);
          }
          if (productData.batchSize) {
            assumptionsArray.push(`Batch Size: ${productData.batchSize}`);
          }
        }

        // Extract machine tonnage from costResult (from backend response)
        // Check multiple locations: machineTonnage_ton, formulaResult.MachineTonnage.requiredTonnage_ton, or AssumptionsJson.MachineTonnage.requiredTonnage_ton
        let machineTonnageTons = 0;
        if (costResult.machineTonnage_ton !== undefined && costResult.machineTonnage_ton !== null) {
          machineTonnageTons = parseFloat(costResult.machineTonnage_ton) || 0;
        } else if (costResult.formulaResult?.MachineTonnage?.requiredTonnage_ton !== undefined) {
          machineTonnageTons = parseFloat(costResult.formulaResult.MachineTonnage.requiredTonnage_ton) || 0;
        } else if (costResult.AssumptionsJson?.MachineTonnage?.requiredTonnage_ton !== undefined) {
          machineTonnageTons = parseFloat(costResult.AssumptionsJson.MachineTonnage.requiredTonnage_ton) || 0;
        }

        // Transform to format expected by ResultsSection
        const transformedCostData = {
          summary: `Should cost analysis for ${productData.productName || "plastic part"} manufactured in ${productData.manufacturingCountry || "location"}. Total cost: $${total.toFixed(4)} per unit.`,
          breakdown: {
            material: material,
            process: process,
            logistics: 0,
            ohp: ohp,
            total_should_cost: total,
            currency: "USD/unit",
          },
          line_items: [],
          key_insights: [],
          assumptions: assumptionsArray,
          _source: "plastics_routes",
          _originalResult: costResult,
          formulaResult: costResult.formulaResult, // Store formula result for assumptions mapping
          cycleTime_s: cycleTimeSeconds, // Store cycle time for PlayBook initialization
          machineTonnage_ton: machineTonnageTons, // Store machine tonnage for PlayBook initialization
          materialPrice_usd_per_kg: materialPricePerKg, // Store material price for assumptions display
        };

        setCostData(transformedCostData);

        // Initialize playBookData with cycle time and machine tonnage from costResult if available
        if (cycleTimeSeconds > 0 || machineTonnageTons > 0) {
          setPlayBookData((prev) => {
            const existingCostParams = prev?.costParams || {};
            // Only update cycle time if it's not set or is 0 (user hasn't manually changed it)
            const shouldUpdateCycleTime = !existingCostParams.cycleTime || existingCostParams.cycleTime === 0;
            // Only update machine tonnage if it's not set or is 0 (user hasn't manually changed it)
            const shouldUpdateMachineTonnage = !existingCostParams.machineTonnage || existingCostParams.machineTonnage === 0;

            return {
              ...(prev || {}),
              costParams: {
                ...existingCostParams,
                cycleTime: shouldUpdateCycleTime ? cycleTimeSeconds : existingCostParams.cycleTime,
                machineTonnage: shouldUpdateMachineTonnage ? machineTonnageTons : existingCostParams.machineTonnage,
              },
            };
          });
        }

        // Navigate to results section
        setActiveSection("results");
        setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

        // Fetch key insights
        await fetchKeyInsights(productData, playBookData);
        // Trigger image generation after Quick Cost completes (non-blocking)
        generateProductImageWithData(productData, playBookData);
      } else {
        throw new Error(costResult.message || "Plastics costing failed");
      }
    } catch (error) {
      console.error("Quick Cost failed:", error);
      alert(`Failed to calculate cost: ${error.message}`);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with  manufacturing parameters, and detailed insights.",
    },
    // {
    //   id: "formulation",
    //   label: "Formulation",
    //   icon: <ScienceIcon />,
    //   disabled: false,
    //   title: "Ready to Build Formulation",
    //   description: "View and edit ingredient composition with functional groups and percentages.",
    // },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

  const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);

  const costTotal = useMemo(() => {
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    console.log(costRows)
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);


  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Mechanical"
          industry="Mechanical"
          commodity="Plastics Molding"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep CookiesQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none", height: "92%" }}>
                <PlasticsQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  initialData={productData}
                />
              </Box>
            )}

            {activeSection === "formulation" && sectionStarted.formulation && (
              <Box>
                <FormulationTable
                  data={recipeData}
                  onRowChange={(index, field, value) => {
                    const updated = [...recipeData];
                    updated[index][field] = value;
                    setRecipeData(updated);
                  }}
                  onRowAdd={() => {
                    setRecipeData([
                      ...recipeData,
                      { ingredient: "", percentage: 0, functional_group: null },
                    ]);
                  }}
                  onRowDelete={(index) => {
                    setRecipeData(recipeData.filter((_, i) => i !== index));
                  }}
                  functionalGroups={functionalGroups}
                  brandColor="#ff6034"
                  editable={true}
                  showTotal={true}
                />
              </Box>
            )}

            {/* Show info notification for playbook section when not started */}
            {!sectionStarted.playbook && activeSection === "playbook" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                <PlasticsPlayBook
                  data={playBookData || (() => {
                    // Extract cycle time and machine tonnage from costData for initial rendering
                    let cycleTimeSeconds = 0;
                    if (costData?.cycleTime_s !== undefined && costData.cycleTime_s !== null) {
                      cycleTimeSeconds = parseFloat(costData.cycleTime_s) || 0;
                    } else if (costData?.formulaResult?.CycleTime?.totalCycleTime_s !== undefined) {
                      cycleTimeSeconds = parseFloat(costData.formulaResult.CycleTime.totalCycleTime_s) || 0;
                    } else if (costData?._originalResult?.cycleTime_s !== undefined) {
                      cycleTimeSeconds = parseFloat(costData._originalResult.cycleTime_s) || 0;
                    } else if (costData?._originalResult?.formulaResult?.CycleTime?.totalCycleTime_s !== undefined) {
                      cycleTimeSeconds = parseFloat(costData._originalResult.formulaResult.CycleTime.totalCycleTime_s) || 0;
                    }

                    let machineTonnageTons = 0;
                    if (costData?.machineTonnage_ton !== undefined && costData.machineTonnage_ton !== null) {
                      machineTonnageTons = parseFloat(costData.machineTonnage_ton) || 0;
                    } else if (costData?.formulaResult?.MachineTonnage?.requiredTonnage_ton !== undefined) {
                      machineTonnageTons = parseFloat(costData.formulaResult.MachineTonnage.requiredTonnage_ton) || 0;
                    } else if (costData?._originalResult?.machineTonnage_ton !== undefined) {
                      machineTonnageTons = parseFloat(costData._originalResult.machineTonnage_ton) || 0;
                    } else if (costData?._originalResult?.formulaResult?.MachineTonnage?.requiredTonnage_ton !== undefined) {
                      machineTonnageTons = parseFloat(costData._originalResult.formulaResult.MachineTonnage.requiredTonnage_ton) || 0;
                    }

                    return {
                      costParams: {
                        cavities: productData?.partDetails?.noOfCavities
                          ? parseInt(productData.partDetails.noOfCavities) || 0
                          : 0,
                        cycleTime: cycleTimeSeconds,
                        machineTonnage: machineTonnageTons > 0
                          ? machineTonnageTons
                          : (productData?.partDetails?.machineTonnage
                            ? parseFloat(productData.partDetails.machineTonnage) || 0
                            : 0),
                        ohpRate: costData ? calculateOhpRateFromCostData(costData) : 0,
                        runnerType: productData?.partDetails?.runnerType || "N/A",
                      }
                    };
                  })()}
                  onChange={handlePlayBookChange}
                />
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    assumptions={costData?.assumptions || []}
                    keyInsights={keyInsightsData}
                    substituteSectionTitle="Plastics Molding Recommendations"
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: costData?.assumptions || [],
                      productData,
                      formulaResult: costData?.formulaResult,
                    })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button */}
        {sectionStarted.product && (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}

        {/* Progressive Analysis Tracker Dialog */}
        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        {/* Legacy Circular Loading (kept for backward compatibility) */}
        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
